import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import { formatCustomDate } from '../../helpers/formatCustomDate';

// ─── Icons ────────────────────────────────────────────────────────────────────
const PrintIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);
const XIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// ─── Info Row ─────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</span>
    <span className="text-sm font-semibold text-gray-800 text-right">
      {value || <span className="text-gray-300 font-normal">—</span>}
    </span>
  </div>
);

// ─── Amount Card ──────────────────────────────────────────────────────────────
const AmountCard = ({ label, amount, color }) => {
  const colors = {
    green: "bg-green-50 border-green-100 text-green-700",
    red: "bg-red-50 border-red-100 text-red-600",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 flex items-center justify-between ${colors[color]}`}>
      <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</span>
      <span className="text-base font-bold">{formatCustomNumber(amount)}</span>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TransactionModal = ({ isOpen, onClose, transaction, bankName, onEdit, onDelete }) => {
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Transaction_${transaction?.account_ref_no || transaction?.journal_number || 'Details'}`,
  });

  return (
    <>
      {/* Invisible backdrop — closes panel on outside click, no visual dim */}
      {isOpen && (
        <div className="fixed inset-0 z-30" onClick={onClose} />
      )}

      {/* ── Side Panel ───────────────────────────────────────────── */}
      <div
        className={`fixed top-0 right-0 h-full w-96 bg-white z-40 flex flex-col
          transition-transform duration-300 ease-in-out
          ${isOpen && transaction ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 24px rgba(0,0,0,0.08)' }}
      >
        {/* ── Header ───────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0 print:hidden">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
              Transaction Details
            </p>
            <h3 className="text-base font-bold text-gray-800">
              {transaction?.account_ref_no || transaction?.journal_number || '—'}
            </h3>
            {bankName && (
              <div className="flex items-center gap-1.5 mt-1">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                <span className="text-xs text-blue-600 font-medium">{bankName}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrint}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
              title="Print"
            >
              <PrintIcon />
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
            >
              <XIcon />
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ──────────────────────────────────── */}
        {transaction && (
          <div ref={printRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

            {/* Print-only header */}
            <div className="hidden print:block mb-4 pb-4 border-b border-gray-300">
              <h1 className="text-xl font-bold text-gray-900 mb-1">Transaction Details</h1>
              {bankName && <p className="text-sm text-gray-500">Account: {bankName}</p>}
              <p className="text-xs text-gray-400">Printed: {formatCustomDate(new Date())}</p>
            </div>

            {/* Type badge + date */}
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
                {transaction.transation_type_name || 'Unknown'}
              </span>
              <span className="text-sm text-gray-500 font-medium">
                {formatCustomDate(transaction.journal_date)}
              </span>
            </div>

            {/* Reference info */}
            <div className="bg-gray-50 rounded-xl p-4">
              <InfoRow label="Account Reference" value={transaction.account_ref_no} />
              <InfoRow label="Journal Number" value={transaction.journal_number} />
              {bankName && <InfoRow label="Bank Account" value={bankName} />}
            </div>

            {/* Financial summary */}
            <div className="space-y-2">
              <AmountCard label="Deposit" amount={transaction.deposit_amount} color="green" />
              <AmountCard label="Withdrawal" amount={transaction.withdraw_amount} color="red" />
              <AmountCard label="Running Balance" amount={transaction.running_balance} color="blue" />
            </div>

            {/* Additional info */}
            {(transaction.description || transaction.notes || transaction.created_by) && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  Additional Info
                </p>
                {transaction.description && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-400 mb-0.5">Description</p>
                    <p className="text-sm text-gray-700">{transaction.description}</p>
                  </div>
                )}
                {transaction.notes && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-400 mb-0.5">Notes</p>
                    <p className="text-sm text-gray-700">{transaction.notes}</p>
                  </div>
                )}
                {transaction.created_by && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Created By</p>
                    <p className="text-sm text-gray-700">{transaction.created_by}</p>
                  </div>
                )}
              </div>
            )}

            {/* Print footer */}
            <div className="hidden print:block mt-6 pt-4 border-t border-gray-300">
              <p className="text-xs text-gray-400 text-center">
                Computer-generated document · No signature required
              </p>
            </div>
          </div>
        )}

        {/* ── Footer Actions ────────────────────────────────────── */}
        {(onEdit || onDelete) && (
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0 print:hidden">
            {onEdit && (
              <button
                onClick={(e) => onEdit(e, transaction)}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition shadow-sm"
              >
                <EditIcon />
                Edit
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => onDelete(e, transaction)}
                className="flex-1 flex items-center justify-center gap-2 bg-white hover:bg-red-50 text-red-600 text-sm font-semibold py-2.5 px-4 rounded-lg border border-red-200 transition"
              >
                <TrashIcon />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TransactionModal;